/**
 * Modern SaaS Dashboard Page
 * Shows simulation stats, running simulations, and quick actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import simulationService from '../../services/simulationService';
import { useAuthStore } from '../../stores/authStore';
import StatusIcon, { getStatusColorClass } from '../../components/StatusIcon';
import { useTranslation } from 'react-i18next';

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [runningSimulations, setRunningSimulations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setError(null);
      const [statsData, runningData] = await Promise.all([
        simulationService.getDashboardStats(),
        simulationService.getRunningSimulations(),
      ]);
      setStats(statsData);
      setRunningSimulations(runningData);
    } catch (err) {
      setError(t('tableauDeBord.erreurChargement'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Auto-refresh if there are running simulations
  useEffect(() => {
    if (runningSimulations.length === 0) return;

    const interval = setInterval(() => {
      loadDashboard();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [runningSimulations.length, loadDashboard]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">
            Welcome back, {user?.first_name || 'User'}
          </h1>
          <p className="text-dark-400 mt-1">
            Here's an overview of your simulations
          </p>
        </div>
        <Link to="/app/simulations/launch" className="btn-primary">
          + New Simulation
        </Link>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
          <button onClick={loadDashboard} className="text-error-400 underline mt-2">
            Retry
          </button>
        </div>
      )}

      {/* Running Simulations Banner */}
      {runningSimulations.length > 0 && (
        <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse mr-3"></div>
              <h2 className="text-lg font-semibold text-dark-100">
                {runningSimulations.length} Running Simulation{runningSimulations.length > 1 ? 's' : ''}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {runningSimulations.map((sim) => (
              <div
                key={sim.simulation_id}
                className="bg-dark-800 rounded-lg p-4 cursor-pointer hover:bg-dark-700 transition-colors"
                onClick={() => navigate(`/app/simulations/${sim.simulation_id}`)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-mono text-dark-300">
                    {sim.simulation_id.substring(0, 20)}...
                  </span>
                  <span className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full">
                    {sim.status}
                  </span>
                </div>
                <p className="text-dark-400 text-sm">
                  {simulationService.getWorkflowDisplayName(sim.workflow_type)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('tableauDeBord.totalSimulations')}
          value={stats?.total_simulations || 0}
          icon="chart"
          color="primary"
        />
        <StatCard
          title="Completed"
          value={stats?.completed || 0}
          icon="check"
          color="success"
        />
        <StatCard
          title="Running"
          value={stats?.running || 0}
          icon="bolt"
          color="warning"
        />
        <StatCard
          title={t('tableauDeBord.tauxReussite')}
          value={`${stats?.success_rate || 0}%`}
          icon="trend"
          color="accent"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Simulations */}
        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-100">{t('tableauDeBord.recentes')}</h2>
            <Link to="/app/simulations" className="text-primary-400 text-sm hover:underline">
              View All →
            </Link>
          </div>
          {stats?.recent_simulations?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_simulations.map((sim) => (
                <div
                  key={sim.simulation_id}
                  className="flex items-center justify-between p-3 bg-dark-900 rounded-lg hover:bg-dark-700 cursor-pointer transition-colors"
                  onClick={() => navigate(`/app/simulations/${sim.simulation_id}`)}
                >
                  <div className="flex items-center">
                    <div className={`mr-3 ${getStatusColorClass(sim.status)}`}>
                      <StatusIcon status={sim.status} className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-dark-100 font-medium text-sm">
                        {sim.simulation_id.substring(0, 25)}...
                      </p>
                      <p className="text-dark-500 text-xs">
                        {simulationService.getWorkflowDisplayName(sim.workflow_type)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: simulationService.getStatusColor(sim.status) + '20',
                        color: simulationService.getStatusColor(sim.status),
                      }}
                    >
                      {sim.status}
                    </span>
                    {sim.duration_seconds && (
                      <p className="text-dark-500 text-xs mt-1">
                        {simulationService.formatDuration(sim.duration_seconds)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-dark-400 mb-4">{t('tableauDeBord.aucuneEncore')}</p>
              <Link to="/app/simulations/launch" className="btn-primary">
                Launch Your First Simulation
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">{t('tableauDeBord.actionsRapides')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <QuickActionCard
              title={t('tableauDeBord.nouvelleSimulation')}
              description={t('tableauDeBord.lancerNouvelle')}
              icon="rocket"
              href="/app/simulations/launch"
            />
            <QuickActionCard
              title={t('tableauDeBord.voirHistorique')}
              description={t('tableauDeBord.parcourirTout')}
              icon="list"
              href="/app/simulations"
            />
            <QuickActionCard
              title={t('tableauDeBord.documentation')}
              description={t('tableauDeBord.enSavoirPlus')}
              icon="book"
              href="/app/docs"
            />
          </div>
        </div>
      </div>

      {/* Workflow Distribution */}
      {stats?.by_workflow && Object.keys(stats.by_workflow).length > 0 && (
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">{t('tableauDeBord.parType')}</h2>
          <div className="space-y-4">
            {Object.entries(stats.by_workflow).map(([workflow, count]) => (
              <div key={workflow} className="flex items-center">
                <span className="text-dark-300 w-48">
                  {simulationService.getWorkflowDisplayName(workflow)}
                </span>
                <div className="flex-1 h-4 bg-dark-700 rounded-full overflow-hidden mx-4">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                    style={{
                      width: `${(count / stats.total_simulations) * 100}%`,
                    }}
                  />
                </div>
                <span className="text-dark-400 w-12 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SVG Icons for Stats
 */
const StatIcons = {
  chart: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  check: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  bolt: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  ),
  trend: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  ),
};

/**
 * Stats Card Component
 */
const StatCard = ({ title, value, icon }) => {
  return (
    <div className="card-dark p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-dark-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-dark-100 mt-1">{value}</p>
        </div>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-dark-700 text-dark-400">
          {StatIcons[icon]}
        </div>
      </div>
    </div>
  );
};

/**
 * SVG Icons for Quick Actions
 */
const QuickActionIcons = {
  rocket: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
    </svg>
  ),
  list: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
  book: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
};

/**
 * Quick Action Card Component
 */
const QuickActionCard = ({ title, description, icon, href, external }) => {
  const content = (
    <div className="bg-dark-900 rounded-lg p-4 hover:bg-dark-700 transition-colors cursor-pointer h-full">
      <div className="w-10 h-10 rounded-lg bg-dark-700 flex items-center justify-center text-dark-300 mb-3">
        {QuickActionIcons[icon]}
      </div>
      <h3 className="text-dark-100 font-medium">{title}</h3>
      <p className="text-dark-500 text-sm">{description}</p>
    </div>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return <Link to={href}>{content}</Link>;
};

export default DashboardPage;
